import { buildSceneArt } from './art.js'
import type { Destination, DestinationId, DestinationVisualTheme, TransportType } from './types.js'

function createVisualTheme(
  skyTop: string,
  skyBottom: string,
  glow: string,
  accent: string,
  horizon: string,
): DestinationVisualTheme {
  return {
    skyTop,
    skyBottom,
    glow,
    accent,
    horizon,
  }
}

const parisPalette = ['transparent', '#87CEEB', '#4682B4', '#2F4F4F', '#228B22', '#D3D3D3', '#A9A9A9']
const cairoPalette = ['transparent', '#87CEEB', '#D2B48C', '#C19A6B', '#8B6914', '#FFF8DC', '#A0522D']
const tokyoPalette = ['transparent', '#FFB7C5', '#FF6B6B', '#8B0000', '#4A7C59', '#87CEEB', '#F5F5DC']
const newYorkPalette = ['transparent', '#87CEEB', '#4682B4', '#5B8C5A', '#8FBC8F', '#C0C0C0', '#FFD700']
const rioPalette = ['transparent', '#87CEEB', '#4A7C59', '#2D5A1B', '#F5F5DC', '#D2B48C', '#1E3A5F']
const sydneyPalette = ['transparent', '#87CEEB', '#4682B4', '#F5F5F5', '#C0C0C0', '#E8E8E8', '#1C4E80']
const nairobiPalette = ['transparent', '#87CEEB', '#D2691E', '#8B6914', '#228B22', '#F4A460', '#DEB887']
const reykjavikPalette = ['transparent', '#0D1B2A', '#00FF88', '#7B2FBE', '#1A6B3C', '#F5F5F5', '#4DFFD2']
const beijingPalette = ['transparent', '#87CEEB', '#6B6B6B', '#8B7355', '#5A5A5A', '#A0A0A0', '#4A4A4A']

export const DESTINATIONS: readonly Destination[] = [
  {
    id: 'paris',
    name: 'Paris',
    country: 'France',
    continent: 'Europe',
    markerEmoji: '🗼',
    markerColor: '#f08a5d',
    coords: { x: 43, y: 34 },
    coastal: true,
    scene: buildSceneArt(parisPalette, [
      '11111111131111111111',
      '11111111333111111111',
      '11111113313311111111',
      '11111133111331111111',
      '11111113666311111111',
      '11111113666311111111',
      '11111113666311111111',
      '11111113666311111111',
      '11111113666311111111',
      '11111133666331111111',
      '11111331666133111111',
      '11113311666113311111',
      '11144111666111441111',
      '11444411313144441111',
      '14444443313344444411',
      '22222222222222222222',
    ]),
    visualTheme: createVisualTheme('#7eaaff', '#f5d6ad', 'rgba(255, 226, 178, 0.44)', '#f08a5d', '#78a46a'),
    facts: ['Paris is the capital city of France.', 'The Eiffel Tower is made of iron and is very tall.', 'People in Paris say bonjour to say hello.'],
    memoryEmoji: '🎨',
    memoryLabel: 'beret',
  },
  {
    id: 'cairo',
    name: 'Cairo',
    country: 'Egypt',
    continent: 'Africa',
    markerEmoji: '🔺',
    markerColor: '#e9b44c',
    coords: { x: 55, y: 42 },
    coastal: false,
    scene: buildSceneArt(cairoPalette, [
      '11111111111111111111',
      '11111111111111111111',
      '11111111311111111111',
      '11111113331111111111',
      '11111133333111131111',
      '11111333333311333111',
      '11113333333333333311',
      '11133333333333333331',
      '11444444444444444441',
      '16611111111111111166',
      '22222222222222222222',
      '22222222222222222222',
      '55555555555555555555',
      '22222222222222222222',
      '22222222222222222222',
      '44444444444444444444',
    ]),
    visualTheme: createVisualTheme('#64baf5', '#f6cf74', 'rgba(255, 241, 190, 0.48)', '#e9b44c', '#9f6d3a'),
    facts: ['Cairo is in Egypt, in Africa.', 'The Great Pyramids stand near Cairo.', 'The pyramids were built over 4,000 years ago.'],
    memoryEmoji: '🪲',
    memoryLabel: 'scarab',
  },
  {
    id: 'tokyo',
    name: 'Tokyo',
    country: 'Japan',
    continent: 'Asia',
    markerEmoji: '🌸',
    markerColor: '#e97da2',
    coords: { x: 84, y: 39 },
    coastal: true,
    scene: buildSceneArt(tokyoPalette, [
      '55555555555555555555',
      '55111155555511115555',
      '51111115555511111155',
      '51111115555511111155',
      '55111155555511115555',
      '55555555555555555555',
      '55555222222222555555',
      '55555522222225555555',
      '55555532555523555555',
      '55555535555535555555',
      '55555535555535555555',
      '55555535555535555555',
      '55555535555535555555',
      '44444434444434444444',
      '44444434444434444444',
      '44444434444434444444',
    ]),
    visualTheme: createVisualTheme('#87cbff', '#f7c4d7', 'rgba(255, 213, 226, 0.42)', '#e97da2', '#69bb7b'),
    facts: ['Tokyo is the capital city of Japan.', 'Cherry trees bloom pink here in spring.', 'Tokyo is one of the biggest cities in the world.'],
    memoryEmoji: '🎐',
    memoryLabel: 'wind chime',
  },
  {
    id: 'new-york',
    name: 'New York',
    country: 'USA',
    continent: 'North America',
    markerEmoji: '🗽',
    markerColor: '#56c2a6',
    coords: { x: 22, y: 39 },
    coastal: true,
    scene: buildSceneArt(newYorkPalette, [
      '11111116111111111111',
      '11111166611111111111',
      '11111113111111111111',
      '11111133311111111111',
      '11111133311111111111',
      '11111133311111111111',
      '11111331331111111111',
      '11111331331111115511',
      '11111333331111555551',
      '11111333331155515155',
      '11114433344155515155',
      '11144433344455555555',
      '22333333333355555555',
      '22222222222222222222',
      '22222222222222222222',
      '22222222222222222222',
    ]),
    visualTheme: createVisualTheme('#7aaefd', '#9fdcff', 'rgba(176, 238, 255, 0.38)', '#56c2a6', '#607a97'),
    facts: ['New York City is in the USA.', 'The Statue of Liberty is green and holds a torch.', 'She stands on a small island in the harbour.'],
    memoryEmoji: '🗽',
    memoryLabel: 'torch',
  },
  {
    id: 'rio',
    name: 'Rio',
    country: 'Brazil',
    continent: 'South America',
    markerEmoji: '🎭',
    markerColor: '#f36b6b',
    coords: { x: 32, y: 68 },
    coastal: true,
    scene: buildSceneArt(rioPalette, [
      '11111111411111111111',
      '11111111411111111111',
      '11111144444111111111',
      '11111111411111111111',
      '11111114441111111111',
      '11111114441111111111',
      '11111114441111111111',
      '11111114441111111111',
      '11111224442211111111',
      '11112224442221111111',
      '11122224442222111111',
      '11222222222222211111',
      '12222222222222221111',
      '33333333333333333111',
      '33333333333333333311',
      '33333333333333333331',
    ]),
    visualTheme: createVisualTheme('#7bd4ff', '#f6b66b', 'rgba(255, 215, 123, 0.42)', '#f36b6b', '#3f8a59'),
    facts: ['Rio de Janeiro is in Brazil.', 'A giant statue looks over the whole city.', 'People dance at a big party called Carnival.'],
    memoryEmoji: '🎭',
    memoryLabel: 'carnival mask',
  },
  {
    id: 'sydney',
    name: 'Sydney',
    country: 'Australia',
    continent: 'Oceania',
    markerEmoji: '🪃',
    markerColor: '#7b9df7',
    coords: { x: 86, y: 73 },
    coastal: true,
    scene: buildSceneArt(sydneyPalette, [
      '11111111111111111111',
      '11111111111111111111',
      '11111111111111111111',
      '11111131111111111111',
      '11111333111131111111',
      '11113333311333111111',
      '11133333333333311111',
      '11334333433343331111',
      '13333433343334333111',
      '55555555555555555555',
      '22222222222222222222',
      '22222222222222222222',
      '66666666666666666666',
      '22222222222222222222',
      '22222222222222222222',
      '66666666666666666666',
    ]),
    visualTheme: createVisualTheme('#81cbff', '#d6f2ff', 'rgba(255, 232, 171, 0.4)', '#7b9df7', '#4e80c2'),
    facts: ['Sydney is the biggest city in Australia.', 'The Opera House roof looks like white sails.', 'Sydney sits right on a beautiful harbour.'],
    memoryEmoji: '🪃',
    memoryLabel: 'boomerang',
  },
  {
    id: 'nairobi',
    name: 'Nairobi',
    country: 'Kenya',
    continent: 'Africa',
    markerEmoji: '🦁',
    markerColor: '#d27f45',
    coords: { x: 60, y: 58 },
    coastal: false,
    scene: buildSceneArt(nairobiPalette, [
      '11111111111111111111',
      '11111111111111111111',
      '11111111111111111111',
      '11111111111111111111',
      '11144444444411111111',
      '11444444444444411111',
      '11144444444441111111',
      '11111311111111111311',
      '11111311111111111311',
      '11111311111111111311',
      '55555555555555555555',
      '55555555555555555555',
      '66666666666666666666',
      '66666666666666666666',
      '22222222222222222222',
      '22222222222222222222',
    ]),
    visualTheme: createVisualTheme('#f6b54c', '#f8de8d', 'rgba(255, 232, 157, 0.45)', '#d27f45', '#6a944a'),
    facts: ['Nairobi is the capital city of Kenya.', 'Lions, elephants, and giraffes live nearby.', 'Flat-topped acacia trees grow on the wide plains.'],
    memoryEmoji: '📿',
    memoryLabel: 'beaded bracelet',
  },
  {
    id: 'reykjavik',
    name: 'Reykjavik',
    country: 'Iceland',
    continent: 'Europe',
    markerEmoji: '🔮',
    markerColor: '#78dce8',
    coords: { x: 38, y: 20 },
    coastal: true,
    scene: buildSceneArt(reykjavikPalette, [
      '11111111111111111111',
      '11222111111111111111',
      '12222211333311116611',
      '11222223333331166611',
      '11122333333333666111',
      '11113336663336661111',
      '11111366666366111111',
      '11111113363111111111',
      '11111111131111111111',
      '11111111111111111111',
      '11551115555111551111',
      '15555155555515555111',
      '55555555555555555511',
      '55555555555555555551',
      '44444444444444444444',
      '44444444444444444444',
    ]),
    visualTheme: createVisualTheme('#152d5f', '#4863c2', 'rgba(97, 240, 211, 0.34)', '#78dce8', '#6a7fdd'),
    facts: ['Reykjavik is the capital city of Iceland.', 'The sky glows green at night — called the northern lights.', 'Hot water bubbles up from the ground here.'],
    memoryEmoji: '🔮',
    memoryLabel: 'snow globe',
  },
  {
    id: 'beijing',
    name: 'Beijing',
    country: 'China',
    continent: 'Asia',
    markerEmoji: '🏮',
    markerColor: '#c75252',
    coords: { x: 72, y: 35 },
    coastal: false,
    scene: buildSceneArt(beijingPalette, [
      '11111111111111111111',
      '11111111111111111111',
      '11111112111111111111',
      '11111122211111221111',
      '11111222221122222111',
      '11112222222222222211',
      '11113333333333333311',
      '11144444444444444441',
      '11445445445445445444',
      '14444444444444444444',
      '44444444444444444444',
      '66666666666666666666',
      '66666666666666666666',
      '44444444444444444444',
      '44444444444444444444',
      '66666666666666666666',
    ]),
    visualTheme: createVisualTheme('#a6d1ff', '#ffd6b1', 'rgba(255, 224, 178, 0.4)', '#c75252', '#9bcf69'),
    facts: ['Beijing is the capital city of China.', 'The Great Wall of China runs over the nearby hills.', 'Red lanterns glow in the streets.'],
    memoryEmoji: '🏮',
    memoryLabel: 'paper lantern',
  },
] as const

export function getDestination(destinationId: DestinationId | null): Destination | null {
  if (!destinationId) return null
  return DESTINATIONS.find((destination) => destination.id === destinationId) ?? null
}

export function getTransportType(from: Destination | null, to: Destination): TransportType {
  if (!from) return 'plane'

  const distance = Math.hypot(to.coords.x - from.coords.x, to.coords.y - from.coords.y)

  if (from.continent === to.continent) {
    return distance < 15 ? 'bus' : 'train'
  }

  if (from.coastal && to.coastal && distance < 34) {
    return 'boat'
  }

  return 'plane'
}